import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { spuCalendarApi, SearchToolResponse, ApiResponse } from '@/lib/api';
import { useMemo } from 'react';

// 查询keys
export const skuCalendarQueryKeys = {
  all: ['skuCalendar'] as const,
  list: (searchId: string, pageSize?: number, pageNo?: number) => 
    ['skuCalendar', 'list', { searchId, pageSize, pageNo }] as const,
};

interface RawDataItem {
  品牌ID: string;
  品牌名称: string;
  SPU编码: string;
  SPU名称: string;
  周日期: string;
  委外在途: number;
}

interface ProcessedItem {
  brandId: string;
  brandName: string;
  spuCode: string;
  spuName: string;
  planDate: string;
  planQuantity: number;
  isEmpty?: boolean;
}

interface ChartDataItem {
  date: string;
  value: number;
  name: string;
  brandName: string;
  isEmpty?: boolean;
}

interface ProcessedData {
  items: ProcessedItem[];
  summary: {
    totalCount: number;
    totalQuantity: number;
  };
  charts: {
    dailyTrend: ChartDataItem[];
  };
}

// 生成指定日期范围内的所有周一日期
const generateWeekDates = (startDate: string, endDate: string): string[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates: string[] = [];

  // 调整到第一个周一
  const firstMonday = new Date(start);
  firstMonday.setDate(start.getDate() - start.getDay() + (start.getDay() === 0 ? -6 : 1));

  // 生成所有周一的日期
  let currentDate = new Date(firstMonday);
  while (currentDate <= end) {
    dates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 7); // 增加一周
  }

  return dates;
};

// 按品牌分组数据
const groupDataByBrand = (data: RawDataItem[]): Record<string, ProcessedItem[]> => {
  return data.reduce((acc: Record<string, ProcessedItem[]>, item: RawDataItem) => {
    const brandName = item['品牌名称'];
    if (!acc[brandName]) {
      acc[brandName] = [];
    }
    acc[brandName].push({
      brandId: item['品牌ID'],
      brandName: item['品牌名称'],
      spuCode: item['SPU编码'],
      spuName: item['SPU名称'],
      planDate: item['周日期'].split('T')[0],
      planQuantity: item['委外在途']
    });
    return acc;
  }, {});
};

// 填充缺失的周数据
const fillMissingWeeks = (brandGroups: Record<string, ProcessedItem[]>, allWeeks: string[]): Record<string, ProcessedItem[]> => {
  const result: Record<string, ProcessedItem[]> = {};

  Object.entries(brandGroups).forEach(([brandName, items]) => {
    const existingDates = new Set(items.map(item => item.planDate));
    result[brandName] = [...items];

    // 添加缺失的周数据
    allWeeks.forEach(weekDate => {
      if (!existingDates.has(weekDate)) {
        result[brandName].push({
          brandId: '',
          brandName: brandName,
          spuName: '无排产计划',
          spuCode: '-',
          planDate: weekDate,
          planQuantity: 0,
          isEmpty: true
        });
      }
    });

    // 按日期排序
    result[brandName].sort((a, b) => a.planDate.localeCompare(b.planDate));
  });

  return result;
};

// 使用SKU排产数据的hook
export function useSkuCalendarData(
  searchId: string,
  pageSize = 50,
  pageNo = 1,
  enabled = true
): UseQueryResult<ApiResponse<SearchToolResponse>, Error> {
  return useQuery({
    queryKey: skuCalendarQueryKeys.list(searchId, pageSize, pageNo),
    queryFn: () => spuCalendarApi.getSkuCalendarData(searchId, pageSize, pageNo),
    enabled: enabled && !!searchId,
    staleTime: 5 * 60 * 1000, // 5分钟
    refetchInterval: 30 * 1000, // 30秒自动刷新
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

// 处理和转换SKU排产数据的hook
export function useProcessedSkuCalendarData(searchId: string, enabled = true) {
  const { data, isLoading, error, refetch } = useSkuCalendarData(searchId, 100, 1, enabled);
  
  const processedData = useMemo(() => {
    if (!data?.res_content?.data_list) {
      return {
        items: [],
        summary: {
          totalCount: 0,
          totalQuantity: 0,
        },
        charts: {
          dailyTrend: [],
          allWeekDates: []
        }
      };
    }
    
    // 验证并转换原始数据
    const rawData = data.res_content.data_list.map(item => {
      const rawItem = item as Record<string, unknown>;
      return {
        品牌ID: String(rawItem['品牌ID'] || ''),
        品牌名称: String(rawItem['品牌名称'] || ''),
        SPU编码: String(rawItem['SPU编码'] || ''),
        SPU名称: String(rawItem['SPU名称'] || ''),
        周日期: String(rawItem['周日期'] || ''),
        委外在途: Number(rawItem['委外在途'] || 0)
      };
    });

    // 转换为标准格式（表格数据）- 保持原始数据不变
    const items = rawData.map(item => ({
      brandId: item.品牌ID,
      brandName: item.品牌名称,
      spuCode: item.SPU编码,
      spuName: item.SPU名称,
      planDate: item.周日期.split('T')[0],
      planQuantity: item.委外在途
    }));

    // 计算汇总数据
    const summary = {
      totalCount: items.length,
      totalQuantity: items.reduce((sum, item) => sum + item.planQuantity, 0),
    };

    // 找出数据的起止日期
    const dates = items.map(item => item.planDate).sort();
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];

    // 生成所有应该显示的周日期
    const allWeekDates = generateWeekDates(startDate, endDate);

    // 直接将每个数据点转换为图表数据，不进行品牌分组
    // 这样可以保持同一品牌同一天不同SPU的独立数据点
    const dailyTrend = items.map(item => ({
      date: item.planDate,
      value: item.planQuantity,
      name: item.spuName,
      brandName: item.brandName,
      spuCode: item.spuCode,
      hasData: true
    }));

    return {
      items, // 保持原始表格数据不变
      summary,
      charts: {
        dailyTrend, // 每个SPU都是独立的数据点
        allWeekDates // 完整的周日期序列用于X轴显示
      }
    };
  }, [data]);

  return {
    data: processedData,
    isLoading,
    error: error as Error | null,
    refetch
  };
} 